import React, { useEffect, useState, useCallback } from 'react';
import { paymentLinkAPI } from '../../services/api';
import { toast } from 'react-toastify';
import {
  Link2, Plus, Copy, CheckCircle2, XCircle,
  Loader2, ExternalLink, RefreshCw, Send, Mail, Phone,
} from 'lucide-react';

const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

const fmtINR = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const STATUS_MAP = {
  active:  { bg: C.successSoft, color: '#065F46', Icon: CheckCircle2 },
  paid:    { bg: C.primarySoft, color: C.primary,  Icon: CheckCircle2 },
  expired: { bg: C.dangerSoft,  color: '#991B1B',  Icon: XCircle      },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status?.toLowerCase()] || STATUS_MAP.expired;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 700, padding: '3px 10px',
      borderRadius: 20, background: s.bg, color: s.color,
    }}>
      <s.Icon size={10} strokeWidth={2.5} />
      {(status || '').charAt(0).toUpperCase() + (status || '').slice(1)}
    </span>
  );
};

const EXPIRY_OPTIONS = [
  { value: '',   label: 'No expiry' },
  { value: '1',  label: '1 day'     },
  { value: '7',  label: '7 days'    },
  { value: '30', label: '30 days'   },
];

const inp = {
  width: '100%', padding: '10px 12px', fontSize: 13, color: '#14142b',
  background: '#f4f4f9', border: '1px solid #e8e8f1',
  borderRadius: 8, outline: 'none', boxSizing: 'border-box',
};
const labelSt = { fontSize: 12, fontWeight: 600, color: '#4a4a6a', marginBottom: 4, display: 'block' };

/* ── Create Link Modal ── */
const CreateModal = ({ onClose, onCreate }) => {
  const [form, setForm]     = useState({ amount: '', description: '', expires_in: '' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.amount || isNaN(form.amount) || Number(form.amount) < 1) {
      errs.amount = 'Amount must be at least ₹1';
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSaving(true);
    try {
      const res = await paymentLinkAPI.create({
        amount:      parseFloat(form.amount),
        description: form.description || undefined,
        expires_in:  form.expires_in  || undefined,
      });
      onCreate(res.data.data);
      toast.success('Payment link created!');
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : err?.response?.data?.error || 'Failed to create link';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: C.surface, borderRadius: C.radius, width: '100%', maxWidth: 460,
        padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,.15)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.ink }}>Create Payment Link</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20 }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelSt}>Amount (₹) *</label>
            <input type="number" min="1" step="0.01" placeholder="e.g. 500"
              value={form.amount}
              onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
              style={{ ...inp, borderColor: errors.amount ? C.danger : C.border }}
            />
            {errors.amount && <p style={{ margin: '4px 0 0', fontSize: 11, color: C.danger }}>{errors.amount}</p>}
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelSt}>Description</label>
            <input type="text" maxLength={500} placeholder="e.g. Payment for invoice #1234"
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              style={inp}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={labelSt}>Expiry</label>
            <select value={form.expires_in}
              onChange={(e) => setForm(f => ({ ...f, expires_in: e.target.value }))}
              style={{ ...inp, cursor: 'pointer' }}
            >
              {EXPIRY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{
              padding: '9px 18px', borderRadius: C.radiusSm, border: `1px solid ${C.border}`,
              background: C.surface, color: C.body, fontSize: 13, cursor: 'pointer',
            }}>Cancel</button>
            <button type="submit" disabled={saving} style={{
              padding: '9px 22px', borderRadius: C.radiusSm, border: 'none',
              background: saving ? C.muted : C.primary, color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              {saving ? <Loader2 size={13} className="spin" /> : <Plus size={13} />}
              {saving ? 'Creating…' : 'Create Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Share Modal ── */
const ShareModal = ({ link, onClose }) => {
  const [form, setForm]     = useState({ business_name: '', email: '', phone: '' });
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.business_name.trim()) errs.business_name = 'Business name is required';
    if (!form.email.trim() && !form.phone.trim()) errs.general = 'Enter at least one of Email or Phone';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email address';
    if (form.phone && !/^[0-9]{10,15}$/.test(form.phone.replace(/\s/g, ''))) errs.phone = 'Enter a valid phone number';
    return errs;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSending(true);
    try {
      const payload = { business_name: form.business_name.trim() };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();

      const res = await paymentLinkAPI.share(link.token, payload);
      toast.success(res.data.message || 'Link sent successfully!');
      if (res.data.errors?.length) {
        res.data.errors.forEach(e => toast.warning(e));
      }
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to send link';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const channels = [];
  if (form.email.trim()) channels.push('Email');
  if (form.phone.trim()) channels.push('SMS');

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.50)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: C.surface, borderRadius: C.radius, width: '100%', maxWidth: 480,
        padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,.18)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Send size={16} color={C.primary} /> Share Payment Link
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 20 }}>×</button>
        </div>

        {/* Link preview */}
        <div style={{ background: C.bg, borderRadius: C.radiusSm, padding: '10px 12px', marginBottom: 20, border: `1px solid ${C.border}` }}>
          <p style={{ margin: 0, fontSize: 11, color: C.muted, marginBottom: 2 }}>Sharing link</p>
          <code style={{ fontSize: 11, color: C.primary, wordBreak: 'break-all' }}>{link.url}</code>
          <p style={{ margin: '6px 0 0', fontSize: 12, color: C.body }}>
            Amount: <strong>{fmtINR(link.amount)}</strong>
            {link.description && <span> · {link.description}</span>}
          </p>
        </div>

        <form onSubmit={handleSend}>
          {/* Business Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelSt}>Business Name *</label>
            <input type="text" placeholder="e.g. My Shop"
              value={form.business_name}
              onChange={(e) => setForm(f => ({ ...f, business_name: e.target.value }))}
              style={{ ...inp, borderColor: errors.business_name ? C.danger : C.border }}
            />
            {errors.business_name && <p style={{ margin: '4px 0 0', fontSize: 11, color: C.danger }}>{errors.business_name}</p>}
            <p style={{ margin: '4px 0 0', fontSize: 11, color: C.muted }}>Shown in the SMS and email sent to customer</p>
          </div>

          {/* Divider */}
          <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>
            Send via — fill one or both
          </p>

          {errors.general && (
            <p style={{ margin: '0 0 12px', fontSize: 12, color: C.danger, background: C.dangerSoft, padding: '8px 12px', borderRadius: C.radiusSm }}>
              {errors.general}
            </p>
          )}

          {/* Email */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ ...labelSt, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Mail size={12} color={C.primary} /> Email Address
            </label>
            <input type="email" placeholder="customer@example.com"
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              style={{ ...inp, borderColor: errors.email ? C.danger : C.border }}
            />
            {errors.email && <p style={{ margin: '4px 0 0', fontSize: 11, color: C.danger }}>{errors.email}</p>}
          </div>

          {/* Phone */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ ...labelSt, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Phone size={12} color={C.primary} /> Mobile Number
            </label>
            <input type="tel" placeholder="10-digit mobile number"
              value={form.phone}
              onChange={(e) => setForm(f => ({ ...f, phone: e.target.value.replace(/[^0-9]/g, '') }))}
              maxLength={15}
              style={{ ...inp, borderColor: errors.phone ? C.danger : C.border }}
            />
            {errors.phone && <p style={{ margin: '4px 0 0', fontSize: 11, color: C.danger }}>{errors.phone}</p>}
            <p style={{ margin: '4px 0 0', fontSize: 11, color: C.muted }}>SMS sent via Twilio · +91 prefix auto-added</p>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{
              padding: '9px 18px', borderRadius: C.radiusSm, border: `1px solid ${C.border}`,
              background: C.surface, color: C.body, fontSize: 13, cursor: 'pointer',
            }}>Cancel</button>
            <button type="submit" disabled={sending} style={{
              padding: '9px 22px', borderRadius: C.radiusSm, border: 'none',
              background: sending ? C.muted : C.primary, color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, minWidth: 120, justifyContent: 'center',
            }}>
              {sending ? <Loader2 size={13} className="spin" /> : <Send size={13} />}
              {sending ? 'Sending…' : channels.length > 0 ? `Send via ${channels.join(' & ')}` : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Main Component ── */
const UserPaymentLinks = () => {
  const [links, setLinks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentLinkAPI.list();
      setLinks(res.data.data || []);
    } catch {
      toast.error('Failed to load payment links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCopy = (url, id) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(id);
      toast.success('Link copied!');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const thStyle = {
    padding: '10px 14px', fontSize: 11, fontWeight: 700,
    color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px',
    textAlign: 'left', borderBottom: `1px solid ${C.border}`,
  };
  const tdStyle = {
    padding: '13px 14px', fontSize: 13, color: C.body,
    borderBottom: `1px solid ${C.borderSoft}`, verticalAlign: 'middle',
  };

  return (
    <div style={{ padding: '24px 20px', maxWidth: 1150, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link2 size={20} color={C.primary} /> Payment Links
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.body }}>
            Create shareable payment links for your customers
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={load} style={{
            padding: '8px 14px', borderRadius: C.radiusSm, border: `1px solid ${C.border}`,
            background: C.surface, color: C.body, fontSize: 13, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={() => setShowCreate(true)} style={{
            padding: '9px 18px', borderRadius: C.radiusSm, border: 'none',
            background: C.primary, color: '#fff', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Plus size={14} /> Create Link
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48, color: C.muted }}>
            <Loader2 size={24} className="spin" />
          </div>
        ) : links.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Link2 size={40} color={C.muted} style={{ marginBottom: 12 }} />
            <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: C.ink }}>No payment links yet</p>
            <p style={{ margin: '6px 0 20px', fontSize: 13, color: C.muted }}>Create your first link to start accepting payments</p>
            <button onClick={() => setShowCreate(true)} style={{
              padding: '9px 20px', borderRadius: C.radiusSm, border: 'none',
              background: C.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <Plus size={13} /> Create Link
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: C.bg }}>
                <tr>
                  {['Payment URL', 'Amount', 'Description', 'Status', 'Expires', 'Created', 'Actions'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {links.map(link => (
                  <tr key={link.id}
                    onMouseEnter={e => e.currentTarget.style.background = C.bg}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={tdStyle}>
                      <code style={{
                        fontSize: 11, background: C.bg, padding: '3px 7px',
                        borderRadius: 4, color: C.primary, maxWidth: 190,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                      }}>
                        {link.url}
                      </code>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: C.ink }}>{fmtINR(link.amount)}</td>
                    <td style={{ ...tdStyle, maxWidth: 180 }}>
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {link.description || <span style={{ color: C.muted }}>—</span>}
                      </span>
                    </td>
                    <td style={tdStyle}><StatusBadge status={link.status} /></td>
                    <td style={{ ...tdStyle, color: link.expires_at ? C.body : C.muted }}>
                      {link.expires_at ? fmtDate(link.expires_at) : 'Never'}
                    </td>
                    <td style={tdStyle}>{fmtDate(link.created_at)}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {/* Copy */}
                        <button onClick={() => handleCopy(link.url, link.id)} title="Copy link"
                          style={{
                            padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`,
                            background: copied === link.id ? C.successSoft : C.surface,
                            color: copied === link.id ? '#065F46' : C.body,
                            fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                          }}
                        >
                          {copied === link.id ? <CheckCircle2 size={11} /> : <Copy size={11} />}
                          {copied === link.id ? 'Copied' : 'Copy'}
                        </button>

                        {/* Share */}
                        {link.status === 'active' && (
                          <button onClick={() => setShareLink(link)} title="Send via Email / SMS"
                            style={{
                              padding: '5px 10px', borderRadius: 6,
                              border: `1px solid ${C.primary}`,
                              background: C.primarySoft, color: C.primary,
                              fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            <Send size={11} /> Share
                          </button>
                        )}

                        {/* Open */}
                        <a href={link.url} target="_blank" rel="noopener noreferrer"
                          style={{
                            padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`,
                            background: C.surface, color: C.body,
                            fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                            textDecoration: 'none',
                          }}
                        >
                          <ExternalLink size={11} /> Open
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreate={(newLink) => setLinks(prev => [newLink, ...prev])}
        />
      )}

      {shareLink && (
        <ShareModal
          link={shareLink}
          onClose={() => setShareLink(null)}
        />
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default UserPaymentLinks;
